import React, { useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import {
  compilePattern,
  compilePatternToText,
} from "../../services/patternCompiler";
import "./PatternCompilation.css";
import Portal from "@mui/material/Portal";

const PatternCompilation = ({ chart, patternTitle, inTheRound = false }) => {
  const [abbreviate, setAbbreviate] = useState(false);
  const compiledInstructions = compilePattern(chart, abbreviate, inTheRound);
  const compiledText = compilePatternToText(compiledInstructions, inTheRound);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  const handleCopyToClipboard = () => {
    const textToCopy = `${patternTitle || "Untitled Pattern"}\n\n${compiledText}`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setSnackbarState({
          open: true,
          message: "Written instructions copied to clipboard",
          severity: "success",
        });
      })
      .catch((err) => {
        setSnackbarState({
          open: true,
          message: "Failed to copy written instructions to clipboard",
          severity: "error",
        });
      });
  };

  return (
    <>
      <Paper variant="woven" className="paper paper--woven">
        <Box className="p-4">
          <Box className="pattern-compilation-header">
            <Typography variant="h5" component="h3" color="primary">
              Written Instructions
            </Typography>
            <Box className="pattern-compilation-actions">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={abbreviate}
                    onChange={(e) => setAbbreviate(e.target.checked)}
                    size="small"
                  />
                }
                label="Abbreviate stitch names"
                sx={{ marginRight: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopy />}
                onClick={handleCopyToClipboard}
                color="secondary"
              >
                Copy
              </Button>
            </Box>
          </Box>

          <Divider className="pattern-compilation-divider" />

          <Box className="pattern-compilation-content">
            {compiledInstructions && compiledInstructions.length > 0 ? (
              <Box className="pattern-compilation-text">
                {compiledInstructions.map((item, index) => (
                  <Box key={index} className="pattern-compilation-line">
                    {item.instruction && item.instruction.trim() !== "" ? (
                      <>
                        <Typography
                          component="span"
                          variant="body1"
                          fontWeight="bold"
                          className="pattern-compilation-row-label"
                        >
                          {item.rowLabel}:
                        </Typography>
                        <Typography
                          component="span"
                          variant="body1"
                          className="pattern-compilation-instruction"
                        >
                          {" "}
                          {item.instruction}.
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        component="span"
                        variant="body1"
                        fontWeight="bold"
                        className="pattern-compilation-row-label"
                      >
                        {item.rowLabel}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                className="pattern-compilation-placeholder"
              >
                Create a pattern to see the written instructions here...
              </Typography>
            )}
          </Box>

          {compiledInstructions && compiledInstructions.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              className="pattern-compilation-footer"
            >
              Instructions are automatically generated from your chart. You can
              copy this text or print it for reference.
            </Typography>
          )}
        </Box>
      </Paper>
      <Portal container={document.body}>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={snackbarState.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarState.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarState.message}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default PatternCompilation;
